import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import googleOAuthService from '../utils/googleOAuthService';

const GoogleOAuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState('Processing...');
    const [error, setError] = useState(null);

    useEffect(() => {
        const handleCallback = async () => {
            try {
                const urlParams = new URLSearchParams(location.search);
                const code = urlParams.get('code');
                const error = urlParams.get('error');

                if (error) {
                    setError('Google authentication was cancelled or failed');
                    setTimeout(() => navigate('/login'), 3000);
                    return;
                }

                if (!code) {
                    setError('No authorization code received');
                    setTimeout(() => navigate('/login'), 3000);
                    return;
                }

                setStatus('Exchanging code for token...');

                // Exchange code for access token
                const redirectUri = `${window.location.origin}/auth/google/callback`;
                const tokenResult = await googleOAuthService.exchangeCodeForToken(code, redirectUri);

                if (!tokenResult.success) {
                    throw new Error(tokenResult.error || 'Failed to exchange code for token');
                }

                setStatus('Getting user information...');

                // Get user info from Google
                const userInfoResult = await googleOAuthService.getUserInfo(tokenResult.access_token);

                if (!userInfoResult.success) {
                    throw new Error(userInfoResult.error || 'Failed to get user information');
                }

                setStatus('Signing you in...');

                // Send access token to backend
                const backendResponse = await axiosInstance.post('/auth/google-signin-access-token', {
                    access_token: tokenResult.access_token
                });

                if (backendResponse.data.success) {
                    // Store token and user data
                    localStorage.setItem('token', backendResponse.data.token);
                    localStorage.setItem('user', JSON.stringify({
                        name: backendResponse.data.user.name,
                        email: backendResponse.data.user.email,
                        role: backendResponse.data.user.role,
                        profile_picture: backendResponse.data.user.profile_picture
                    }));

                    setStatus('Success! Redirecting...');

                    // Navigate based on user role
                    if (backendResponse.data.user.role === 'admin') {
                        localStorage.setItem('admin_token', backendResponse.data.token);
                        setTimeout(() => navigate('/admin-dashboard'), 1000);
                    } else {
                        setTimeout(() => navigate('/dashboard'), 1000);
                    }
                } else {
                    throw new Error(backendResponse.data.message || 'Backend authentication failed');
                }

            } catch (error) {
                console.error('Google OAuth callback error:', error);
                setError(error.message || 'Authentication failed');
                setTimeout(() => navigate('/login'), 3000);
            }
        };

        handleCallback();
    }, [location, navigate]);

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#000',
            color: '#fff',
            fontFamily: 'Inter, sans-serif'
        }}>
            <div style={{
                textAlign: 'center',
                padding: '2rem',
                maxWidth: '400px'
            }}>
                {error ? (
                    <>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
                        <h2 style={{ marginBottom: '1rem' }}>Authentication Failed</h2>
                        <p style={{ color: '#71717a', marginBottom: '1rem' }}>{error}</p>
                        <p style={{ color: '#71717a', fontSize: '0.875rem' }}>
                            Redirecting to login page...
                        </p>
                    </>
                ) : (
                    <>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                        <h2 style={{ marginBottom: '1rem' }}>Processing</h2>
                        <p style={{ color: '#71717a', marginBottom: '1rem' }}>{status}</p>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid #3f3f46',
                            borderTop: '3px solid #fff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto'
                        }}></div>
                    </>
                )}
            </div>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default GoogleOAuthCallback; 